import React, { Component } from "react";
import { WitchMoon } from "./Art";

// The witch's riddles: three quick questions, each a real piece of behavioural
// science with a Halloween twist. Right or wrong, everyone gets the treat at
// the end. The answer is told after each one, because the point is the
// "huh, really?" rather than the score.

interface Question {
  question: string;
  options: string[];
  answer: number;
  explanation: string;
}

export const QUESTIONS: Question[] = [
  {
    question:
      "Trick-or-treaters are told to take one sweet each, then left alone with the bowl. Who is most likely to take extra?",
    options: ["Children who came on their own", "Children in a group, whose names nobody asked", "Children dressed as witches"],
    answer: 1,
    explanation:
      "In a 1976 Halloween study, 57% of children in anonymous groups took extra, against 7.5% of children on their own who'd given their names. A crowd and a costume loosen the rules. It's called deindividuation.",
  },
  {
    question:
      "Researchers asked trick-or-treaters their names, then put one thing behind the sweet bowl. Taking extra fell from 38% to 9%. What was it?",
    options: ["A mirror", "A scary mask", "A sign saying \"Please take one\""],
    answer: 0,
    explanation:
      "A mirror. Seeing yourself, once someone knows who you are, makes you act like the person you'd like to be. Oddly, it did nothing for the children who stayed anonymous.",
  },
  {
    question: "Why might a horror film make a good first date?",
    options: ["Nobody can see you blush in the dark", "A racing heart gets mistaken for attraction", "Shared popcorn"],
    answer: 1,
    explanation:
      "In a famous 1974 study, men who took a woman's number on a wobbly suspension bridge called her far more often (9 of 18) than men who met her on a steady one (2 of 16). We may misread our own pounding hearts.",
  },
  {
    question: "Two jars of identical biscuits: one with two left, one with ten. Which biscuits do people like more?",
    options: ["The ones from the jar of ten", "The ones from the jar of two", "No difference"],
    answer: 1,
    explanation:
      "In a 1975 study, biscuits from the nearly empty jar were liked more, though they tasted no different. Scarcity makes things feel more valuable, which is why \"only a few left\" sells.",
  },
  {
    question: "Offered a small toy or a sweet, what do trick-or-treaters choose?",
    options: ["Sweets, almost every time", "Toys, about as often as sweets", "Neither, they want both"],
    answer: 1,
    explanation:
      "In a Halloween experiment, children picked toys about as often as sweets. What we assume people want and what they actually choose are often two different things.",
  },
];

const pickThree = (): Question[] => {
  const shuffled = [...QUESTIONS];
  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const swap = Math.floor(Math.random() * (index + 1));
    [shuffled[index], shuffled[swap]] = [shuffled[swap], shuffled[index]];
  }
  return shuffled.slice(0, 3);
};

const VERDICTS = [
  "Not one. The witch is cackling, but she's soft really.",
  "One out of three. The witch has seen worse.",
  "Two out of three. The witch is quietly impressed.",
  "Three out of three. The witch wants to know where you studied.",
];

interface Props {
  onDone: () => void;
}

interface State {
  questions: Question[];
  index: number;
  chosen: number | null;
  score: number;
  finished: boolean;
}

class Riddle extends Component<Props, State> {
  state: State = { questions: pickThree(), index: 0, chosen: null, score: 0, finished: false };

  choose = (option: number) => {
    if (this.state.chosen !== null) return;

    const correct = option === this.state.questions[this.state.index].answer;
    this.setState((state) => ({ chosen: option, score: state.score + (correct ? 1 : 0) }));
  };

  next = () => {
    if (this.state.index === this.state.questions.length - 1) {
      this.setState({ finished: true });
      return;
    }
    this.setState((state) => ({ index: state.index + 1, chosen: null }));
  };

  render() {
    const { questions, index, chosen, score, finished } = this.state;

    if (finished) {
      return (
        <div className="hw-trick" aria-live="polite">
          <WitchMoon />
          <p className="hw-eyebrow">The witch's verdict</p>
          <h2>{VERDICTS[score]}</h2>
          <p className="hw-lead">Every trick ends in a treat. Yours is waiting.</p>
          <button className="hw-btn hw-btn--orange" type="button" onClick={this.props.onDone}>
            Claim my treat
          </button>
        </div>
      );
    }

    const question = questions[index];
    const answered = chosen !== null;

    return (
      <div className="hw-trick">
        {index === 0 && !answered && <WitchMoon />}
        <p className="hw-eyebrow">
          Trick: the witch's riddles · {index + 1} of {questions.length}
        </p>
        <h2 className="hw-riddle__question">{question.question}</h2>
        <div className="hw-options" role="group" aria-label="Your answer">
          {question.options.map((option, optionIndex) => {
            const state = !answered
              ? ""
              : optionIndex === question.answer
              ? "is-right"
              : optionIndex === chosen
              ? "is-wrong"
              : "is-faded";
            return (
              <button
                key={option}
                type="button"
                className={`hw-option ${state}`}
                onClick={() => this.choose(optionIndex)}
                disabled={answered}
              >
                {option}
              </button>
            );
          })}
        </div>
        {answered && (
          <div className="hw-answer" aria-live="polite">
            <p>
              <strong>{chosen === question.answer ? "Right!" : "Not quite."}</strong> {question.explanation}
            </p>
            <button className="hw-btn hw-btn--orange" type="button" onClick={this.next}>
              {index === questions.length - 1 ? "And the verdict is…" : "Next riddle"}
            </button>
          </div>
        )}
      </div>
    );
  }
}

export default Riddle;
